'use client';

import React from 'react';
import styles from './DeliveredLandingMockup.module.scss';
import { gaClick, gaCta } from '@/src/helpers/gtag';

const deliverItems = [
  {
    title: 'Strategy',
    text: 'We align vision with real business goals.',
    bullets: ['Discovery & research', 'Roadmapping', 'Outcome-led planning'],
  },
  {
    title: 'Build',
    text: 'We build with precision and enterprise-grade standards.',
    bullets: ['UX & design', 'Engineering', 'Quality assurance'],
  },
  {
    title: 'Delivery',
    text: 'We deliver with clarity, care, and accountability.',
    bullets: ['Deployment', 'Enablement', 'Continuous support'],
  },
];

const projects = [
  {
    title: 'Apex Infrastructure',
    category: 'Enterprise platform',
    result: 'Improved lead quality and brand perception.',
  },
  {
    title: 'Northbay Finance',
    category: 'Custom operations tool',
    result: 'Reduced manual work by 68%.',
  },
  {
    title: 'Luma Bedding',
    category: 'Shopify build',
    result: 'Increased revenue by 112% in 6 months.',
  },
];

const process = [
  ['01', 'Discover', 'We listen, learn, and align on outcomes that matter.'],
  ['02', 'Plan', 'We define the right strategy and roadmap.'],
  ['03', 'Build', 'We design, build, and test with precision.'],
  ['04', 'Deliver', 'We launch, refine, and support for the long run.'],
];

export default function DeliveredLandingMockup() {
  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <a className={styles.logo} href="#">
          <span className={styles.logoMark}>D</span>
          <span>DELIVERED.</span>
        </a>

        <nav className={styles.nav}>
          <a href="#why">Why Us</a>
          <a href="#process">Our Process</a>
          <a href="#projects">Projects</a>
          <a href="#about">About</a>
          <a href="#insights">Insights</a>
        </nav>

        <a
          className={styles.topCta}
          href="#contact"
          onClick={() => gaCta("Let's Build Together", 'delivered_mockup_top_cta', '#contact')}
        >
          Let’s Build Together
        </a>
      </header>

      <section className={styles.hero}>
        <div className={styles.heroText}>
          <p className={styles.eyebrow}>WE START. WE BUILD. WE FINISH.</p>

          <h1>
            End-to-end
            <br />
            delivery.
            <span>You get results.</span>
          </h1>

          <p className={styles.lead}>
            A small team of high-performing experts, experienced in delivering enterprise solutions
            — so you get results you can count on.
          </p>

          <div className={styles.actions}>
            <a
              href="#projects"
              className={styles.primaryBtn}
              onClick={() => gaCta('See Our Work', 'delivered_mockup_primary', '#projects')}
            >
              See Our Work <span>→</span>
            </a>
            <a
              href="#contact"
              className={styles.secondaryBtn}
              onClick={() => gaCta('Book a Call', 'delivered_mockup_secondary', '#contact')}
            >
              ☎ Book a Call
            </a>
          </div>
        </div>

        <div className={styles.heroVisual} aria-hidden="true">
          <div className={styles.founderImage}>
            <img src="/images/founder.jpg" alt="" />
          </div>

          <svg className={styles.lightRibbon} viewBox="0 0 900 420">
            <path d="M20 330 C210 210, 390 400, 570 250 C700 140, 770 190, 880 70" fill="none" />
            <path d="M60 370 C260 260, 410 410, 620 285 C740 210, 785 180, 870 150" fill="none" />
          </svg>
        </div>
      </section>

      <section className={styles.deliverSection}>
        <p className={styles.sectionLabel}>WHAT WE DELIVER</p>

        <div className={styles.deliverGrid}>
          {deliverItems.map((item, index) => (
            <article className={styles.deliverCard} key={item.title}>
              <div className={styles.cardTop}>
                <div className={styles.number}>0{index + 1}</div>
                <div className={styles.diagram}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>

              <h2>{item.title}</h2>
              <p>{item.text}</p>

              <ul>
                {item.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.workSection} id="projects">
        <div className={styles.sectionHeader}>
          <p className={styles.sectionLabel}>SELECTED WORK</p>
          <a href="#" onClick={() => gaClick('delivered_mockup_view_all_projects', { href: '#' })}>
            View all projects →
          </a>
        </div>

        <div className={styles.projectGrid}>
          {projects.map((project, index) => (
            <article className={styles.projectCard} key={project.title}>
              <div className={`${styles.projectPreview} ${styles[`preview${index}`]}`}>
                <div className={styles.fakeBrowser}>
                  <span />
                  <span />
                  <span />
                </div>
                <h3>{project.category}</h3>
              </div>

              <div className={styles.projectBody}>
                <h2>{project.title}</h2>
                <p>{project.result}</p>
                <a
                  href="#"
                  onClick={() =>
                    gaClick('delivered_mockup_case_study', { title: project.title, href: '#' })
                  }
                >
                  View case study →
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.processSection} id="process">
        <p className={styles.sectionLabel}>OUR PROCESS</p>

        <div className={styles.processLine}>
          {process.map(([step, title, text]) => (
            <article className={styles.processStep} key={step}>
              <span>{step}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.proofSection}>
        <div className={styles.quote}>
          <p>
            “Delivered felt like an extension of our team. They brought clarity, speed, and
            exceptional craftsmanship to every step of the process.”
          </p>
          <span>Sarah Mitchell — Head of Operations</span>
        </div>

        <div className={styles.metrics}>
          <div>
            <strong>98%</strong>
            <span>Projects delivered on time & budget</span>
          </div>
          <div>
            <strong>4.9/5</strong>
            <span>Average client satisfaction</span>
          </div>
          <div>
            <strong>68%</strong>
            <span>Average improvement in key outcomes</span>
          </div>
        </div>
      </section>

      <section className={styles.finalCta} id="contact">
        <h2>
          Ready to build something
          <br />
          great <em>together?</em>
        </h2>

        <div>
          <p>Let’s talk about your goals and how we can help you achieve them.</p>
          <div className={styles.actions}>
            <a
              href="#"
              className={styles.primaryBtn}
              onClick={() => gaCta('Book a Call', 'delivered_mockup_final_primary', '#')}
            >
              Book a Call
            </a>
            <a
              href="#"
              className={styles.secondaryBtn}
              onClick={() => gaCta('Start a Project', 'delivered_mockup_final_secondary', '#')}
            >
              Start a Project →
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
